import { memo, useMemo } from 'react'
import type { AnalyticsSeriesPoint } from '../../../../shared/types/analytics'

interface LinkClickSparklineProps {
  points: AnalyticsSeriesPoint[]
  /** 요일 이름을 뽑을 언어 (i18n 의 resolvedLanguage) */
  language: string
}

/** 가장 큰 막대를 100% 로 두고 나머지를 비율로 깎는다 */
export function barHeightPercent(clicks: number, max: number): number {
  if (max <= 0) {
    return 0
  }
  return Math.round((clicks / max) * 100)
}

/** 'YYYY-MM-DD' 를 요일 이름으로. 형식이 어긋나면 원문을 쓴다. */
function weekdayLabel(date: string, language: string): string {
  const parsed = new Date(`${date}T00:00:00`)
  if (Number.isNaN(parsed.getTime())) {
    return date
  }
  return new Intl.DateTimeFormat(language, { weekday: 'short' }).format(parsed)
}

/**
 * 최근 며칠간의 클릭을 막대로 보여 주는 작은 그래프.
 * 축·범례 없이 추세만 읽는 용도라 차트 라이브러리를 쓰지 않는다.
 */
function LinkClickSparkline({ points, language }: LinkClickSparklineProps) {
  const max = useMemo(
    () => points.reduce((highest, point) => Math.max(highest, point.clicks), 0),
    [points]
  )

  if (points.length === 0) {
    return null
  }

  return (
    <ul className="flex h-24 items-end gap-2" role="list">
      {points.map((point, index) => (
        <li
          key={point.date}
          className="flex h-full flex-1 flex-col items-center justify-end gap-1"
        >
          <span className="text-[10px] tabular-nums text-gray-500 dark:text-gray-400">
            {point.clicks}
          </span>
          <span
            // 정적인 높이라 레이아웃 변동을 만들지 않는다
            style={{ height: `${barHeightPercent(point.clicks, max)}%` }}
            className={`min-h-[3px] w-full rounded ${
              index === points.length - 1
                ? 'bg-primary-600'
                : 'bg-primary-200 dark:bg-primary-900'
            }`}
          />
          <span className="text-[10px] text-gray-400">
            {weekdayLabel(point.date, language)}
          </span>
        </li>
      ))}
    </ul>
  )
}

export default memo(LinkClickSparkline)
